import { useRead, ReadState, isCreators } from "@/components/LoadingStates";
import LiveDemoBadge, { isLiveCreator } from "@/components/LiveDemoBadge";
import MediaImage from "@/components/MediaImage";
import IncludeDemoToggle from "@/components/IncludeDemoToggle";
import useIncludeDemo from "@/hooks/useIncludeDemo";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@/context/AuthContext";

import SectionTabs, { SECTION_TABS } from "@/components/SectionTabs";
import AddCreatorDialog from "@/components/AddCreatorDialog";
import { Button } from "@/components/ui/button";
import { GitCompare, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import PlatformBadge from "@/components/PlatformBadge";
import { formatNumber, classNames } from "@/lib/format";

const PLATFORMS = [
  { key: "all", label: "All" },
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
  { key: "youtube", label: "Shorts" },
];

export default function Creators() {
  const { canEdit, user } = useAuth();
  const [platform, setPlatform] = useState("all");
  const [q, setQ] = useState("");
  const [includeDemo, setIncludeDemo] = useIncludeDemo(user?.workspace_id);
  const [addOpen, setAddOpen] = useState(false);
  const addInvoker = useRef(null);
  const creatorsRead = useRead(`/creators?platform=${platform}`, isCreators);
  const currentRead = useRef(creatorsRead);
  currentRead.current = creatorsRead;
  const loadCreators = useCallback(() => currentRead.current.reload(), []);
  const creators = useMemo(() => creatorsRead.data || [], [creatorsRead.data]);
  const filtered = useMemo(() => creators.filter((c) => (includeDemo || isLiveCreator(c)) && (!q || c.name.toLowerCase().includes(q.toLowerCase()) || c.handle.toLowerCase().includes(q.toLowerCase()))), [creators, includeDemo, q]);
  const space = user?.workspace_id || "personal";
  const [selection, setSelection] = useState({ space, ids: [] });
  const visibleIds = useMemo(() => new Set(filtered.map((c) => c.id)), [filtered]);
  const selected = selection.space === space && creatorsRead.status === "success" ? selection.ids.filter((id) => visibleIds.has(id)) : [];
  useEffect(() => {
    setSelection((previous) => {
      const ids = previous.space === space ? (creatorsRead.status === "success" ? previous.ids.filter((id) => visibleIds.has(id)) : previous.ids) : [];
      return previous.space === space && ids.length === previous.ids.length && ids.every((id, i) => id === previous.ids[i]) ? previous : { space, ids };
    });
  }, [space, creatorsRead.status, visibleIds]);
  const toggleSelection = (id) => {
    if (creatorsRead.status !== "success" || !visibleIds.has(id)) return;
    setSelection({ space, ids: selected.includes(id) ? selected.filter((x) => x !== id) : selected.length < 2 ? [...selected, id] : selected });
  };
  const openAdd = (event) => { if (!canEdit) return; addInvoker.current = event.currentTarget; setAddOpen(true); };
  const hasFilters = platform !== "all" || !!q || !includeDemo;
  const clearFilters = () => { setPlatform("all"); setQ(""); setIncludeDemo(true); };

  return (
    <div className="space-y-6" data-testid="creators-page">
      <SectionTabs tabs={SECTION_TABS.creators} activeKey="monitored" ariaLabel="Creators sections" data-testid="section-tabs-creators" />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Creators</h1>
          <p className="text-muted-foreground text-sm mt-1">Every creator in your watchlists.</p>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <Button asChild variant="outline" className="flex-1 sm:flex-none">
            <Link to="/compare" data-testid="creators-compare-button"><GitCompare className="mr-2 h-4 w-4" aria-hidden="true" />Compare creators</Link>
          </Button>
          {canEdit && (
            <AddCreatorDialog onCreated={loadCreators} open={addOpen} onOpenChange={setAddOpen} returnFocusRef={addInvoker}>
              <Button className="w-full sm:w-auto" data-testid="creators-add-button" onClick={(event) => { addInvoker.current = event.currentTarget; }}><Plus className="mr-2 h-4 w-4" aria-hidden="true" />Add Creator</Button>
            </AddCreatorDialog>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          {PLATFORMS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPlatform(p.key)}
              data-testid={`creator-platform-${p.key}`}
              className={classNames(
                "px-3 py-1.5 text-xs rounded-md transition-colors font-medium",
                platform === p.key ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Input
          placeholder="Filter by name or handle…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
          data-testid="creators-filter-input"
        />
      </div>

      <IncludeDemoToggle checked={includeDemo} onChange={setIncludeDemo} />
      <p className="text-xs text-muted-foreground" data-testid="demo-filter-scope">{filtered.length} éléments affichés sur {creators.length} reçus.</p>
      <div className="flex flex-wrap items-center gap-3">
        <p role="status" aria-live="polite" data-testid="creators-selection-status" className="text-sm">{creatorsRead.status !== "success" && selection.space === space && selection.ids.length ? "Sélection en attente de la lecture courante." : `${selected.length}/2 créateurs sélectionnés. Sélectionnez deux créateurs distincts pour comparer.`}</p>
        {selected.length === 2 && <Button asChild><Link data-testid="creators-compare-selected" to={`/compare?${new URLSearchParams({ a: selected[0], b: selected[1] })}`}>Comparer la sélection (2) →</Link></Button>}
      </div>
      <ReadState read={creatorsRead} scope="creators" empty={filtered.length === 0} filtered={hasFilters} title={!includeDemo && creators.length ? "Aucun créateur Live parmi les résultats reçus" : undefined} description={hasFilters ? "Modifiez vos filtres ou réincluez le contenu de démo." : canEdit ? "Ajoutez un créateur pour commencer à suivre ses vidéos." : "Aucun créateur disponible. Un Owner ou Editor peut en ajouter."} primaryCta={hasFilters ? { label: "Effacer les filtres", onClick: clearFilters } : canEdit ? { label: "Add Creator", onClick: openAdd } : undefined}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((c) => (
          <div key={c.id} className="relative min-w-0">
          <label className="mb-1 flex items-center gap-2 text-sm"><input type="checkbox" data-testid={`creator-select-${c.id}`} checked={selected.includes(c.id)} disabled={creatorsRead.status !== "success" || (!selected.includes(c.id) && selected.length === 2)} onChange={() => toggleSelection(c.id)} aria-describedby="creators-selection-description" />Sélectionner {c.name} pour comparer</label>
          <Link
            to={`/creators/${c.id}`}
            data-testid={`creator-card-${c.id}`}
            className="block"
          >
            <Card className="p-4 hover:border-primary/60 transition-colors">
              <div className="flex items-center gap-3">
                <MediaImage src={c.avatar} alt="" name={c.name} kind="avatar" demo={!isLiveCreator(c)} className="h-12 w-12 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {c.name}
                    <LiveDemoBadge creator={c} legacyLiveId={`live-badge-${c.id}`} />
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{c.handle}</div>
                </div>
                <PlatformBadge platform={c.platform} />
              </div>
              <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="font-mono text-sm font-bold">{c.followers_available === false ? "Indisponible" : formatNumber(c.followers)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Followers</div>
                </div>
                <div>
                  <div className="font-mono text-sm font-bold">{formatNumber(c.avg_views)}</div><p className="text-[10px] text-muted-foreground">{c.video_count} vidéos · {c.coauthor_count || 0} collaborations</p>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Baseline médiane</div>
                </div>
                <div>
                  <div className="text-xs font-medium truncate">{c.niche}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Niche</div>
                </div>
              </div>
            </Card>
          </Link>
          </div>
        ))}
      </div>
      </ReadState>
      <p id="creators-selection-description" className="sr-only">Deux créateurs maximum. Désélectionnez un créateur pour en choisir un autre.</p>
    </div>
  );
}
